import { Spinner, TextInput } from 'flowbite-react';
import { HiOutlineSearch, HiOutlineRefresh } from 'react-icons/hi';
import { formatRelative } from './trace-utils';
import SessionPager from './session-pager';

/**
 * Left panel: traced sessions of the app.
 */
export default function SessionList({
    sessions,
    loading,
    selectedSessionId,
    onSelect,
    search,
    onSearchChange,
    onRefresh,
    pageNumber,
    pageSize,
    onPageSizeChange,
    rangeStart,
    rangeEnd,
    hasPrevious,
    hasNext,
    onPrevious,
    onNext,
}) {
    // Search filters the loaded page client-side, so the pager would be
    // describing a different set of rows than the one on screen. Hide it while
    // a search is active rather than show a misleading range.
    const searching = Boolean(search.trim());

    return (
        <div className="flex h-full min-h-0 flex-col border-r border-gray-200 bg-gray-50">
            <div className="border-b border-gray-200 bg-white px-4 py-3">
                <div className="flex items-center justify-between pb-3">
                    <h2 className="text-sm font-semibold text-gray-900">
                        Sessions
                        {sessions.length > 0 && (
                            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                {sessions.length}
                            </span>
                        )}
                    </h2>
                    <button
                        type="button"
                        onClick={onRefresh}
                        title="Refresh sessions"
                        className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <HiOutlineRefresh size={16} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>
                <TextInput
                    icon={HiOutlineSearch}
                    placeholder="Search sessions"
                    sizing="sm"
                    value={search}
                    onChange={event => onSearchChange(event.target.value)}
                />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
                {loading && sessions.length === 0 && (
                    <div className="flex justify-center py-10">
                        <Spinner size="md" />
                    </div>
                )}

                {!loading && sessions.length === 0 && (
                    <p className="px-4 py-10 text-center text-sm text-gray-500">
                        {search
                            ? 'No sessions match your search.'
                            : 'No traced sessions yet. Only sessions with recorded trace events appear here.'}
                    </p>
                )}

                <ul className="divide-y divide-gray-200">
                    {sessions.map(session => {
                        const selected = session.sessionId === selectedSessionId;
                        return (
                            <li key={session.sessionId}>
                                <button
                                    type="button"
                                    onClick={() => onSelect(session)}
                                    className={`w-full border-l-2 px-4 py-3 text-left transition-colors ${selected
                                        ? 'border-indigo-600 bg-indigo-50'
                                        : 'border-transparent hover:bg-white'
                                        }`}
                                >
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className="truncate text-sm font-medium text-gray-900">
                                            {session.refId ? `#${session.refId}` : session.sessionId}
                                        </span>
                                        <span className="shrink-0 text-xs text-gray-400">
                                            {formatRelative(session.modifiedAt || session.createdAt)}
                                        </span>
                                    </div>
                                    <p className="mt-1 truncate text-xs text-gray-500">
                                        {session.displayMessage || session.sessionId}
                                    </p>
                                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                        {session.spanCount > 0 && (
                                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                                                {session.spanCount} {session.spanCount === 1 ? 'span' : 'spans'}
                                            </span>
                                        )}
                                        {session.errorCount > 0 && (
                                            <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                                                {session.errorCount} {session.errorCount === 1 ? 'error' : 'errors'}
                                            </span>
                                        )}
                                        {session.handleBy && (
                                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                                                {session.handleBy}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>

            </div>

            {!searching && (
                <SessionPager
                    pageNumber={pageNumber}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    pageSize={pageSize}
                    onPageSizeChange={onPageSizeChange}
                    hasPrevious={hasPrevious}
                    hasNext={hasNext}
                    onPrevious={onPrevious}
                    onNext={onNext}
                    loading={loading}
                />
            )}
        </div>
    );
}
